import { useApi } from './api';
import type {
  BatchSaveClientSurveysRequest,
  BatchSaveClientSurveysResponse,
  ClientSurveyRecord,
  PatchSurveyTemplateRequest,
  ReplaceSurveyTemplateResult,
  SurveyTemplateDetail,
  SurveyTemplateSummary,
  SurveyTemplateWriteDto
} from '../types/survey';

async function parseSurveyApiError(res: Response, fallback: string): Promise<Error> {
  try {
    const body = await res.json();
    if (body?.error) return new Error(String(body.error));
    if (body && typeof body === 'object') {
      const fieldMessages = Object.entries(body)
        .filter(([, value]) => typeof value === 'string')
        .map(([key, value]) => `${key}: ${value}`);
      if (fieldMessages.length > 0) return new Error(fieldMessages.join(' '));
    }
  } catch {
    // ignore json parse errors
  }
  return new Error(fallback);
}

export const useSurveyService = () => {
  const { apiFetch } = useApi();

  const getSurveyTemplates = async (active?: boolean): Promise<SurveyTemplateSummary[]> => {
    const params = new URLSearchParams();
    if (typeof active === 'boolean') params.append('active', String(active));
    const query = params.toString();
    const res = await apiFetch(`/survey-templates${query ? `?${query}` : ''}`);
    if (!res.ok) throw await parseSurveyApiError(res, 'No se pudieron obtener las plantillas de encuesta');
    return res.json();
  };

  const getSurveyTemplateById = async (id: number): Promise<SurveyTemplateDetail> => {
    const res = await apiFetch(`/survey-templates/${id}`);
    if (!res.ok) throw await parseSurveyApiError(res, 'No se pudo obtener el detalle de la plantilla');
    return res.json();
  };

  const createSurveyTemplate = async (
    payload: SurveyTemplateWriteDto
  ): Promise<SurveyTemplateDetail> => {
    const res = await apiFetch('/survey-templates', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    if (!res.ok) throw await parseSurveyApiError(res, 'No se pudo crear la plantilla de encuesta');
    return res.json();
  };

  const replaceSurveyTemplate = async (
    id: number,
    payload: SurveyTemplateWriteDto
  ): Promise<ReplaceSurveyTemplateResult> => {
    const res = await apiFetch(`/survey-templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
    if (!res.ok) throw await parseSurveyApiError(res, 'No se pudo guardar la plantilla de encuesta');
    const detail = (await res.json()) as SurveyTemplateDetail;
    return {
      detail,
      status: res.status === 201 ? 201 : 200,
      createdAsNewVersion: Boolean(detail.createdAsNewVersion)
    };
  };

  const patchSurveyTemplate = async (
    id: number,
    payload: PatchSurveyTemplateRequest
  ): Promise<SurveyTemplateDetail> => {
    const res = await apiFetch(`/survey-templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload)
    });
    if (!res.ok) throw await parseSurveyApiError(res, 'No se pudo actualizar la plantilla de encuesta');
    return res.json();
  };

  const batchSaveClientSurveys = async (
    payload: BatchSaveClientSurveysRequest
  ): Promise<BatchSaveClientSurveysResponse> => {
    const res = await apiFetch('/client-surveys/batch-save', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      throw await parseSurveyApiError(res, 'No se pudo guardar la encuesta');
    }
    return res.json();
  };

  const getClientSurveysByPhoneCall = async (phoneCallId: number): Promise<ClientSurveyRecord[]> => {
    const res = await apiFetch(`/client-surveys/by-phone-call/${phoneCallId}`);
    if (!res.ok) throw new Error('No se pudieron obtener las encuestas de la llamada');
    return res.json();
  };

  return {
    getSurveyTemplates,
    getSurveyTemplateById,
    createSurveyTemplate,
    replaceSurveyTemplate,
    patchSurveyTemplate,
    batchSaveClientSurveys,
    getClientSurveysByPhoneCall
  };
};
